import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { FieldKey, FORM_CONFIG } from '../connection-zoom-form.config';
import { CommonPopupBodyFooterLayout } from '../../../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { ConnectionType } from '../../../../../../../../api/connections.dao';
import Text from '../../../../../../../global/text/text';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import { SelectGroup } from '../../../../../../../global/select-group/select-group';
import { cancelText, submitText } from '../../../../../../../../constants/common-labels.constants';
import { ApprovalType } from '../../../../../../../_events/_manage-event/shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { FormFieldProps } from '../../../../../../../../infrastructure/form-viewmodel-controller';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import { FormFieldError } from '../../../../../../../global/form/form-field-input/form-field-error';
import {
    ZOOM_APPROVAL_TYPE_CONNECTION_LABEL_TEXT,
    ZOOM_AUTO_REGISTRATION_ENABLED_CONNECTION_LABEL_TEXT,
    ZOOM_REGISTRANTS_CONFIRMATION_EMAIL_CONNECTION_LABEL_TEXT,
    ZOOM_REGISTRATION_ENABLED_CONNECTION_LABEL_TEXT,
    ZOOM_SYNC_ENABLED_CONNECTION_LABEL_TEXT
} from '../../conections-list.texts';
import { ConnectionZoomMeetingFormVM } from './connection-zoom-meeting-form.viewmodel';
import { ZOOM_MEETING_FORM_CONFIG } from './connection-zoom-meeting-form.config';

export type ConnectionTemplateFormPresentationProps = {
    vm: ConnectionZoomMeetingFormVM;
};

export const ConnectionZoomMeetingFormPresentation = observer(({ vm }: ConnectionTemplateFormPresentationProps): JSX.Element => {
    const {
        type,
        label,
        zoomGroupId,
        autoRegistrationEnabled,
        autoSyncEnabled,
        approvalType,
        registrationEnabled,
        registrantsConfirmationEmail,
        meetingUniqueReference
    } = vm.getFormFields(FieldKey, [FieldKey.AUTO_RECORDING, FieldKey.PANELIST_LINK_CREATION_ENABLED]);

    const toggleField = (field: FormFieldProps) => {
        field.setValue(!field.value);
    };

    const toggleAutoRegField = (field: FormFieldProps) => {
        if (!field.value) {
            [autoSyncEnabled, registrationEnabled].forEach(f => f.setValue(true));
            approvalType.setValue(ApprovalType.AUTOMATICALLY);
            registrantsConfirmationEmail.setValue(false);
        }

        toggleField(field);
    };

    const toggleAutoSyncField = (field: FormFieldProps) => {
        if (!field.value) {
            registrantsConfirmationEmail.setValue(false);
            registrationEnabled.setValue(true);
            approvalType.setValue(ApprovalType.AUTOMATICALLY);
        }

        toggleField(field);
    };

    const toggleApprovalType = () => {
        if (approvalType.value === ApprovalType.AUTOMATICALLY) {
            approvalType.setValue(ApprovalType.MANUALLY);
        } else {
            approvalType.setValue(ApprovalType.AUTOMATICALLY);
        }
    };

    const getFieldPrefix = (value: boolean): string => {
        return value ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
    };

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form oe-form--connection-template'>
                    <FormFieldInput.Text
                        field={type}
                        labelDescriptor={ZOOM_MEETING_FORM_CONFIG[FieldKey.TYPE].labelDescriptor}
                        readOnly
                        disabled
                    />

                    <FormFieldInput.Text field={label} labelDescriptor={ZOOM_MEETING_FORM_CONFIG[FieldKey.LABEL].labelDescriptor} withError />

                    <FormFieldInput.Select
                        field={meetingUniqueReference}
                        labelDescriptor={FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].labelDescriptor}
                        customOptions={
                            FORM_CONFIG[FieldKey.MEETING_UNIQUE_REFERENCE].valueOptions.map((item, i) => {
                                const isObject = typeof item === 'object' && item != null;
                                return <option value={isObject ? item.value : item as number} key={i}>{isObject ? item.label : item}</option>;
                            })
                        }
                        fieldClassName='m-bottom-20px'
                    />

                    <FormField
                        label={<FormattedMessage {...ZOOM_MEETING_FORM_CONFIG[FieldKey.GROUP].labelDescriptor} />}
                        className='event-access-selector m-bottom-20px'
                    >
                        <SelectGroup
                            value={zoomGroupId.value as number}
                            type={ConnectionType.Webinar}
                            onChange={groupId => zoomGroupId.setValue(groupId)}
                            onBlur={zoomGroupId.setTouched}
                            className='field w-100 oe-form-field__control'
                            disabled={vm.isEditMode}
                            skipDefaultValue
                        />
                        <FormFieldError field={zoomGroupId} labelDescriptor={ZOOM_MEETING_FORM_CONFIG[FieldKey.GROUP].labelDescriptor} />
                    </FormField>
                    {vm.showAutoRegAndAutoSyncToggles && (
                        <>
                            {!vm.isV2ConnectionVersion() && <FormFieldInput.Toggle
                                isSelected={autoRegistrationEnabled.value as boolean}
                                toggle={toggleAutoRegField.bind(null, autoRegistrationEnabled)}
                                text={<Text
                                    {...ZOOM_AUTO_REGISTRATION_ENABLED_CONNECTION_LABEL_TEXT}
                                    values={{
                                        prefix: getFieldPrefix(autoRegistrationEnabled.value as boolean)
                                    }}
                                />}
                            />}
                            <FormFieldInput.Toggle
                                isSelected={autoSyncEnabled.value as boolean}
                                toggle={toggleAutoSyncField.bind(null, autoSyncEnabled)}
                                isDisabled={autoRegistrationEnabled.value as boolean}
                                text={<Text
                                    {...ZOOM_SYNC_ENABLED_CONNECTION_LABEL_TEXT}
                                    values={{
                                        prefix: getFieldPrefix(autoSyncEnabled.value as boolean)
                                    }}
                                />}
                            />
                        </>
                    )}
                    <FormFieldInput.Toggle
                        isSelected={registrationEnabled.value as boolean}
                        toggle={toggleField.bind(null, registrationEnabled)}
                        isDisabled={autoRegistrationEnabled.value as boolean || autoSyncEnabled.value as boolean}
                        text={<Text
                            {...ZOOM_REGISTRATION_ENABLED_CONNECTION_LABEL_TEXT}
                            values={{
                                prefix: getFieldPrefix(registrationEnabled.value as boolean)
                            }}
                        />}
                    />

                    {registrationEnabled.value && <>
                        <FormFieldInput.Toggle
                            isSelected={approvalType.value === ApprovalType.AUTOMATICALLY}
                            toggle={toggleApprovalType}
                            isDisabled={autoRegistrationEnabled.value as boolean || autoSyncEnabled.value as boolean}
                            text={<Text
                                {...ZOOM_APPROVAL_TYPE_CONNECTION_LABEL_TEXT}
                                values={{
                                    prefix: getFieldPrefix(approvalType.value === ApprovalType.AUTOMATICALLY)
                                }}
                            />}
                        />

                        <FormFieldInput.Toggle
                            isSelected={registrantsConfirmationEmail.value as boolean}
                            toggle={toggleField.bind(null, registrantsConfirmationEmail)}
                            isDisabled={autoSyncEnabled.value as boolean}
                            text={<Text
                                {...ZOOM_REGISTRANTS_CONFIRMATION_EMAIL_CONNECTION_LABEL_TEXT}
                                values={{
                                    prefix: getFieldPrefix(registrantsConfirmationEmail.value as boolean)
                                }}
                            />}
                        />
                    </>}
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={vm.submitForm}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                </div>
            }
        />
    );
});
